import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

export default function CompanyRegisterPage() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    companyName: "",
    facilityLocation: "",
    facilitySize: "small",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
  });

  const [error, setError] = useState("");

  function handleChange(field: string, value: string) {
    setFormData(prev => ({ ...prev, [field]: value }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    // Validate required fields
    if (!formData.companyName || !formData.contactName || !formData.contactEmail) {
      setError("Please fill in all required fields");
      return;
    }

    // Store company info in sessionStorage and proceed to package selection
    sessionStorage.setItem("companyRegistration", JSON.stringify(formData));
    navigate("/register/package");
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-16">
      <div className="w-full max-w-2xl">
        <div className="rounded-xl border border-border bg-card p-8 shadow-lg">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-foreground hover:opacity-90">
              <img src="/logo.png" alt="CompostDeveloper" className="h-10 w-10 rounded" />
              <span className="text-xl font-bold">
                Compost<span className="text-forest-500">Developer</span>
              </span>
            </Link>
            <h1 className="mt-6 text-2xl font-semibold text-foreground">Company Registration</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Tell us about your composting facility
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-400">
                {error}
              </div>
            )}

            {/* Company Information */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-foreground">Facility Information</h2>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  value={formData.companyName}
                  onChange={(e) => handleChange("companyName", e.target.value)}
                  placeholder="e.g., Green Valley Composting"
                  className="w-full px-4 py-3 bg-secondary border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-forest-600 text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Facility Location
                </label>
                <input
                  value={formData.facilityLocation}
                  onChange={(e) => handleChange("facilityLocation", e.target.value)}
                  placeholder="e.g., Phoenix, AZ"
                  className="w-full px-4 py-3 bg-secondary border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-forest-600 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Facility Size
                </label>
                <select
                  value={formData.facilitySize}
                  onChange={(e) => handleChange("facilitySize", e.target.value)}
                  className="w-full px-4 py-3 bg-secondary border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-forest-600 text-sm"
                >
                  <option value="small">Small (&lt; 500 cubic yards/month)</option>
                  <option value="medium">Medium (500-2000 cubic yards/month)</option>
                  <option value="large">Large (2000-10,000 cubic yards/month)</option>
                  <option value="very_large">Very Large (&gt; 10,000 cubic yards/month)</option>
                </select>
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4 pt-6 border-t border-border">
              <h2 className="text-lg font-semibold text-foreground">Primary Contact</h2>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  value={formData.contactName}
                  onChange={(e) => handleChange("contactName", e.target.value)}
                  placeholder="Your name"
                  className="w-full px-4 py-3 bg-secondary border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-forest-600 text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => handleChange("contactEmail", e.target.value)}
                  placeholder="you@company.com"
                  className="w-full px-4 py-3 bg-secondary border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-forest-600 text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.contactPhone}
                  onChange={(e) => handleChange("contactPhone", e.target.value)}
                  placeholder="(555) 123-4567"
                  className="w-full px-4 py-3 bg-secondary border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-forest-600 text-sm"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Link
                to="/"
                className="flex-1 px-6 py-3 border border-border rounded-lg text-foreground hover:bg-secondary transition-colors text-center font-medium"
              >
                Back to Home
              </Link>
              <button
                type="submit"
                className="flex-1 px-6 py-3 bg-forest-600 hover:bg-forest-700 text-white rounded-lg font-medium transition-colors"
              >
                Continue to Packages
              </button>
            </div>

            <div className="text-center text-sm text-muted-foreground pt-4">
              Already have an account?{" "}
              <Link to="/login" className="text-forest-600 hover:text-forest-700 font-medium">
                Sign in
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
